/**
 * tutorCompleteProfileViewModel — ce que « Compléter mon profil » lit dans le store et dans l'état de
 * l'écran.
 *
 * Patron A : le compte connecté (`currentUser.object`, que `submitCompleteProfile` remplace au succès),
 * les matières (`staticData.school_subjects`), la semaine de « Ma dispo » (`tutorAvailability.weekView`,
 * demandée par `useTutorAvailabilityData`) et le brouillon gardé d'un passage précédent
 * (`formData`, l'ancien `saveFormData` du parcours, sous une clé à lui : sa forme n'est pas celle de
 * l'ancien formulaire).
 *
 * Le brouillon rendu est le compte, recouvert du brouillon gardé puis des réponses modifiées de ce
 * passage. La semaine : celle servie, ses créneaux proposés remplacés par ceux du brouillon (`null`
 * suit le serveur), sa date par la date choisie. Les deux règles des créneaux comptent la semaine
 * ENTIÈRE, engagés compris : un tuteur dont l'unique créneau accueille un live n'a pas vidé sa dispo.
 *
 * Un `createSelector` sur les tranches qu'il lit et sur chaque champ de l'état de l'écran.
 */
import { createSelector } from "reselect"

import {
    approvedClassesOf,
    availabilityNeedsAdvice,
    toTutorProfileCatalogs,
    toTutorProfileDraft,
    toTutorProfilePreview,
    type ApiSchoolSubject,
    type ApiTutorProfileUser,
} from "../../../adapters/tutor/Account/tutorProfileAdapter"
import {
    engagedSlotsOf,
    openSlotsOf,
    releasedSlotsOf,
    startingDateOpensOn,
    toUITutorWeek,
    type ApiTutorWeekView,
} from "../../../adapters/tutor/shared/tutorWeekAdapter"
import { type UITutorWeek } from "../../../ui/types/tutorDispoOffres"
import {
    type UITutorProfileCatalogs,
    type UITutorProfileDraft,
    type UITutorProfilePreview,
} from "../../../ui/types/tutorCompte"
import { selectAccount } from "../../shared/displayedStudent"

/** La clé du brouillon gardé (`state.data.formData`). */
export const TUTOR_PROFILE_FORM_ID = "tutor-complete-profile"

export type TutorCompleteProfileInputs = {
    edits: Partial<UITutorProfileDraft>
    openSlots: string[] | null
    startingDate: string | null
}

export type TutorCompleteProfileViewModel = {
    /** Le compte n'est pas encore là : les réponses déjà données ne sont pas pré-remplies. */
    loading: boolean
    userId: number | null
    draft: UITutorProfileDraft
    /** Le brouillon gardé d'un passage précédent, recouvert de ce passage : ce que la sortie garde. */
    keptEdits: Partial<UITutorProfileDraft>
    catalogs: UITutorProfileCatalogs
    approvedClasses: boolean
    preview: UITutorProfilePreview
    /** `null` tant que la semaine n'est pas là : l'éditeur ne se pose pas sans elle. */
    week: UITutorWeek | null
    weekLoadFailed: boolean
    startingDateOpensOn: string
    /** Les créneaux proposés tels que l'envoi les postera (les engagés restent au serveur). */
    openSlots: string[]
    serverOpenSlots: string[]
    startingDate: string | null
    /** Proposés ET engagés : « Ajoute au moins un créneau » ne retient qu'une semaine vide. */
    weekSlotCount: number
    availabilityAdvice: boolean
}

// Le store est en JS non typé : on lit les champs qu'on connaît, sans le décrire en entier.
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type ReduxState = any

const EMPTY_EDITS: Partial<UITutorProfileDraft> = {}

const selectUser = (state: ReduxState): ApiTutorProfileUser | null => selectAccount(state) as ApiTutorProfileUser | null
const selectSubjects = (state: ReduxState): ApiSchoolSubject[] | undefined => state?.data?.staticData?.school_subjects
const selectWeek = (state: ReduxState): ApiTutorWeekView | null => state?.data?.tutorAvailability?.weekView ?? null
const selectWeekError = (state: ReduxState): unknown => state?.api?.getAvailability?.error ?? null
const selectKeptForms = (state: ReduxState): Record<string, Partial<UITutorProfileDraft> | null> | undefined =>
    state?.data?.formData?.[TUTOR_PROFILE_FORM_ID]

const selectCatalogs = createSelector([selectUser, selectSubjects], (user, subjects) =>
    toTutorProfileCatalogs(subjects, approvedClassesOf(user))
)

const selectAccountDraft = createSelector([selectUser], (user) => (user?.id ? toTutorProfileDraft(user) : null))

const selectKept = createSelector(
    [selectUser, selectKeptForms],
    (user, kept) => (user?.id != null ? kept?.[String(user.id)] : null) ?? EMPTY_EDITS
)

type WeekPart = Pick<
    TutorCompleteProfileViewModel,
    | "week"
    | "weekLoadFailed"
    | "startingDateOpensOn"
    | "openSlots"
    | "serverOpenSlots"
    | "startingDate"
    | "weekSlotCount"
    | "availabilityAdvice"
>

/** La semaine à part : taper une réponse ne la redessine pas, seuls ses créneaux et sa date la changent. */
const selectWeekPart = createSelector(
    [
        selectWeek,
        selectWeekError,
        (_state: ReduxState, inputs: TutorCompleteProfileInputs) => inputs.openSlots,
        (_state: ReduxState, inputs: TutorCompleteProfileInputs) => inputs.startingDate,
    ],
    (week, weekError, draftSlots, draftDate): WeekPart => {
        const serverOpenSlots = openSlotsOf(week)
        const openSlots = draftSlots ?? serverOpenSlots
        const startingDate = draftDate ?? week?.availability_starting_date ?? null
        const engaged = [...engagedSlotsOf(week), ...releasedSlotsOf(week)].map((slot) => slot.slot as string)
        const weekSlots = [...openSlots, ...engaged]
        return {
            week: week == null ? null : { ...toUITutorWeek(week, openSlots), startingDate },
            weekLoadFailed: week == null && weekError != null,
            startingDateOpensOn: startingDateOpensOn(startingDate),
            openSlots,
            serverOpenSlots,
            startingDate,
            weekSlotCount: weekSlots.length,
            availabilityAdvice: availabilityNeedsAdvice(weekSlots),
        }
    }
)

const selectProfile = createSelector(
    [
        selectUser,
        selectAccountDraft,
        selectKept,
        selectCatalogs,
        selectWeekPart,
        (_state: ReduxState, inputs: TutorCompleteProfileInputs) => inputs.edits,
    ],
    (user, accountDraft, kept, catalogs, weekPart, edits): TutorCompleteProfileViewModel => {
        const keptEdits = { ...kept, ...edits }
        const draft = { ...(accountDraft ?? toTutorProfileDraft({})), ...keptEdits }
        return {
            loading: accountDraft == null,
            userId: user?.id ?? null,
            draft,
            keptEdits,
            catalogs,
            approvedClasses: approvedClassesOf(user).length > 0,
            preview: toTutorProfilePreview(user ?? {}, draft),
            ...weekPart,
        }
    }
)

export function tutorCompleteProfileViewModel(
    state: unknown,
    inputs: TutorCompleteProfileInputs
): TutorCompleteProfileViewModel {
    return selectProfile(state, inputs)
}
